import { useState, useEffect, useCallback, useRef } from "react";
import { GROUP_MATCHES, PLAYOFF_MATCHES, GROUPS, ROUND_LABELS, TOURNAMENT_WINNER } from "./matches";
import { calcPoints, isValidScore, isMatchDone } from "./scoring";
import { loadInitialData, loadAllPredictions, loadPlayerPredictions, savePrediction, saveWinnerPrediction, writeValue, MATCH_TO_SHEET_EXPORT, playoffMatchIdToRow } from "./sheets";
const MATCH_TO_SHEET = typeof MATCH_TO_SHEET_EXPORT !== 'undefined' ? MATCH_TO_SHEET_EXPORT : {};
import "./App.css";

const Icon = {
  Trophy:  () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9H4a2 2 0 0 1-2-2V5h4"/><path d="M18 9h2a2 2 0 0 0 2-2V5h-4"/><path d="M12 17v4"/><path d="M8 21h8"/><path d="M6 9a6 6 0 0 0 12 0V3H6z"/></svg>,
  Pencil:  () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  Check:   () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  Refresh: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
  Shield:  () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Grid:    () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
  User:    () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  Home:    () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  Bar:     () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  ChevronDown:  () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>,
  ChevronRight: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>,
  Home: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  Bar:  () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
};

function ptsBadge(pts, max) {
  if (pts === null || pts === undefined) return "";
  if (pts === max) return "pts-max";
  if (pts >= 3) return "pts-good";
  if (pts > 0) return "pts-partial";
  return "pts-zero";
}

const isPlaceholder = (name) => /^Mängija\d+$/i.test(name?.trim() || "");


// ── Dashboard ("Minu seis") ────────────────────────────────────────────────
function DashboardView({ player, playerIndex, players, groupResults, playoffResults,
                         playerPredictions, standings, winnerPrediction, loading, onTabSwitch, allPlayerData }) {
  if (!player || isPlaceholder(player)) {
    return (
      <div className="empty-state">
        <span className="empty-icon">⚽</span>
        <p>Vali ülalt oma nimi et näha oma seisu</p>
      </div>
    );
  }

  const allMatches = [...GROUP_MATCHES, ...PLAYOFF_MATCHES];

  const getResult = (match) => {
    if (match.round) {
      return playoffResults[match.id] || "";
    }
    return groupResults[match.id] || "";
  };

  const getPred = (matchId) => playerPredictions[`${player}_${matchId}`] || null;

  // Täna mängitavad — sisaldab ka homme öösel/hommikul (kuni 06:00) toimuvaid mänge,
  // kuna need kuuluvad sisuliselt "tänase õhtu" mänguplokki (kickoff pärast keskööd)
  const today = new Date();
  const todayStr = `${String(today.getDate()).padStart(2,"0")}.${String(today.getMonth()+1).padStart(2,"0")}`;
  const tomorrow = new Date(today.getTime() + 24*60*60*1000);
  const tomorrowStr = `${String(tomorrow.getDate()).padStart(2,"0")}.${String(tomorrow.getMonth()+1).padStart(2,"0")}`;
  const allMatchesSorted = [...GROUP_MATCHES]
    .sort((a, b) => (MATCH_TO_SHEET[a.id] ?? 999) - (MATCH_TO_SHEET[b.id] ?? 999))
    .concat(PLAYOFF_MATCHES);
  const todayMatches = allMatchesSorted.filter(m => {
    const inWindow = m.date === todayStr ||
      (m.date === tomorrowStr && parseInt(m.time.split(":")[0], 10) < 6);
    if (!inWindow) return false;
    return !isMatchDone(getResult(m)); // jäta välja juba lõppenud mängud (need on "Viimased tulemused" all)
  });

  // Järgmised ennustamata mängud
  const unanswered = [...GROUP_MATCHES]
    .sort((a, b) => (MATCH_TO_SHEET[a.id] ?? 999) - (MATCH_TO_SHEET[b.id] ?? 999))
    .filter(m => !isMatchDone(getResult(m)) && !getPred(m.id));
  const nextUnanswered = unanswered.slice(0, 3);

  // Oma koht edetabelis
  // standings uses Mängija1..22 placeholders, players array has real names
  const myPlayerIdx = players ? players.indexOf(player) : -1;
  const myStanding = myPlayerIdx >= 0 ? standings[myPlayerIdx] : null;
  const sortedReal = (allPlayerData && allPlayerData.length > 0)
    ? allPlayerData
        .filter(p => !isPlaceholder(p.name))
        .map(p => {
          let total = 0;
          allMatches.forEach(m => {
            const pred = p.predictions?.[m.id];
            if (!pred?.score) return;
            const result = getResult(m);
            if (!isMatchDone(result)) return;
            total += calcPoints(result, pred.score, pred.nep, !!m.round) || 0;
          });
          return {
            name: p.name,
            total: total + ((TOURNAMENT_WINNER && p.winner &&
              p.winner.trim().toLowerCase() === TOURNAMENT_WINNER.trim().toLowerCase()) ? 7 : 0),
          };
        })
        .sort((a, b) => b.total - a.total)
    : [];
  const myRank = player
    ? sortedReal.findIndex(s => s.name === player) + 1
    : null;
  const realCount = sortedReal.length;

  // Viimased tulemused (mängitud, millel ennustus olemas)
  const recentDone = [...GROUP_MATCHES]
    .sort((a, b) => (MATCH_TO_SHEET[a.id] ?? 999) - (MATCH_TO_SHEET[b.id] ?? 999))
    .concat(PLAYOFF_MATCHES)
    .filter(m => isMatchDone(getResult(m)) && getPred(m.id))
    .slice(-5).reverse();

  // Arvuta punktid otse ennustustest+tulemustest, kui KOKKU lehe andmed (myStanding) on
  // puudulikud/nullis hoolimata tehtud ennustustest — Sheetsi valemid võivad ajutiselt
  // katki minna kui worldcup26.ir kirjutab poolelioleva mängu skoori
  let calculatedGroupPts = 0, calculatedPlayoffPts = 0;
  allMatches.forEach(m => {
    const pred = getPred(m.id);
    const result = getResult(m);
    if (!pred || !isMatchDone(result)) return;
    const pts = calcPoints(result, pred.score, pred.nep, !!m.round) || 0;
    if (m.round) calculatedPlayoffPts += pts; else calculatedGroupPts += pts;
  });
  const calculatedPts = calculatedGroupPts + calculatedPlayoffPts;

  // +7p boonus kui turniirivõitja ennustus oli õige (arvestatud alles pärast finaali)
  const winnerBonus = (TOURNAMENT_WINNER && winnerPrediction &&
    winnerPrediction.trim().toLowerCase() === TOURNAMENT_WINNER.trim().toLowerCase()) ? 7 : 0;

  // KOKKU lehe SORT/LARGE valemid lähevad katki viigis punktide korral (#REF! vead),
  // mis nihutab ridu ja teeb standings massiivi ebausaldusväärseks. Arvutame
  // "Minu seis" punktid alati otse ennustustest+tulemustest.
  const hasAnyPrediction = allMatches.some(m => getPred(m.id));
  const totalPts = calculatedPts + winnerBonus;
  const maxPossible = recentDone.length > 0
    ? recentDone.reduce((acc, m) => acc + (!!m.round ? 7 : 5), 0) : 0;
  const earnedPts = recentDone.reduce((acc, m) => {
    const pred = getPred(m.id);
    if (!pred) return acc;
    return acc + (calcPoints(getResult(m), pred.score, pred.nep, !!m.round) || 0);
  }, 0);

  const medals = ["🥇","🥈","🥉"];

  return (
    <div className="dashboard">
      {/* Seis kaart */}
      <div className="dash-row">
        <div className="dash-card dash-rank">
          <div className="dash-label">Koht</div>
          <div className="dash-big">{myRank > 0 ? (medals[myRank-1] || `#${myRank}`) : "—"}</div>
          <div className="dash-sub">{myRank > 0 ? `${myRank}/${realCount}` : totalPts > 0 ? `—/${realCount}` : "andmed puuduvad"}</div>
        </div>
        <div className="dash-card dash-pts">
          <div className="dash-label">Punktid</div>
          <div className="dash-big">{totalPts}</div>
          <div className="dash-sub">AG {calculatedGroupPts} · PO {calculatedPlayoffPts}{winnerBonus > 0 ? ` · 🏆 +${winnerBonus}` : ""}</div>
        </div>
        <div className="dash-card dash-winner">
          <div className="dash-label">Võitja</div>
          <div className="dash-big" style={{fontSize: winnerPrediction ? "16px" : "24px"}}>
            {winnerPrediction || "—"}
          </div>
          <div className="dash-sub">+7p täpse eest</div>
        </div>
      </div>

      {/* Täna mängud */}
      {todayMatches.length > 0 && (
        <div className="dash-section">
          <div className="dash-section-title">Täna mängitakse</div>
          {todayMatches.map(m => {
            const pred = getPred(m.id);
            const result = getResult(m);
            const pts = result && pred ? calcPoints(result, pred.score, pred.nep, !!m.round) : null;
            return (
              <div key={m.id} className="dash-match">
                <div className="dash-match-teams">{m.home} — {m.away}</div>
                <div className="dash-match-right">
                  <span className="dash-match-time">{m.time}</span>
                  {pred
                    ? <span className={`pts-badge ${pts !== null ? ptsBadge(pts, !!m.round ? 7 : 5) : "pts-partial"}`}>
                        {pred.score}{pred.nep ? ` ${pred.nep}` : ""}
                        {pts !== null ? ` · ${pts}p` : ""}
                      </span>
                    : <span className="dash-no-pred" onClick={() => onTabSwitch("predictions")}>
                        Ennusta →
                      </span>
                  }
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Ennustamata mängud */}
      {nextUnanswered.length > 0 && (
        <div className="dash-section">
          <div className="dash-section-title">
            Ennustamata ({unanswered.length} mängu)
            <button className="dash-link" onClick={() => onTabSwitch("predictions")}>Vaata kõiki →</button>
          </div>
          {nextUnanswered.map(m => (
            <div key={m.id} className="dash-match">
              <div className="dash-match-teams">{m.home} — {m.away}</div>
              <div className="dash-match-right">
                <span className="dash-match-time">{m.date} {m.time}</span>
                <span className="dash-no-pred" onClick={() => onTabSwitch("predictions")}>Ennusta →</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Viimased tulemused */}
      {recentDone.length > 0 && (
        <div className="dash-section">
          <div className="dash-section-title">Viimased tulemused</div>
          {recentDone.map(m => {
            const pred = getPred(m.id);
            const result = getResult(m);
            const pts = pred ? calcPoints(result, pred.score, pred.nep, !!m.round) : null;
            return (
              <div key={m.id} className="dash-match">
                <div>
                  <div className="dash-match-teams">{m.home} — {m.away}</div>
                  <div className="dash-match-sub">Tulemus: {result} · Ennustus: {pred?.score||"—"}</div>
                </div>
                <span className={`pts-badge ${ptsBadge(pts, !!m.round ? 7 : 5)}`}>{pts}p</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Statistikavaade ────────────────────────────────────────────────────────
function StatsView({ allPlayerData, groupResults, playoffResults, loading }) {
  if (loading) return <div className="loading">Laen statistikat…</div>;

  const realPlayers = allPlayerData.filter(p => !isPlaceholder(p.name));
  const allMatches  = [...GROUP_MATCHES, ...PLAYOFF_MATCHES];

  const getResult = (match) => {
    if (match.round) {
      return playoffResults[match.id] || "";
    }
    return groupResults[match.id] || "";
  };

  const doneMatches = allMatches.filter(m => isMatchDone(getResult(m)));
  if (doneMatches.length === 0) {
    return <div className="empty-state"><span className="empty-icon">📊</span><p>Statistika ilmub pärast esimesi mänge</p></div>;
  }

  // Per-player stats
  const stats = realPlayers.map(p => {
    let totalPts = 0, exactScores = 0, correctOutcomes = 0,
        nepCorrect = 0, nepTotal = 0, predicted = 0;
    doneMatches.forEach(m => {
      const pred = p.predictions?.[m.id];
      if (!pred?.score) return;
      predicted++;
      const result = getResult(m);
      const isPlayoff = !!m.round;
      const pts = calcPoints(result, pred.score, pred.nep, isPlayoff);
      totalPts += pts || 0;
      const rParts = result.split(" ")[0].split("-").map(Number);
      const pParts = pred.score.split("-").map(Number);
      if (rParts.length === 2 && pParts.length === 2) {
        const rOut = rParts[0] > rParts[1] ? 1 : rParts[0] < rParts[1] ? -1 : 0;
        const pOut = pParts[0] > pParts[1] ? 1 : pParts[0] < pParts[1] ? -1 : 0;
        if (rOut === pOut) correctOutcomes++;
        if (rParts[0] === pParts[0] && rParts[1] === pParts[1]) exactScores++;
      }
      if (isPlayoff && pred.nep) {
        nepTotal++;
        if (result.includes(pred.nep)) nepCorrect++;
      }
    });
    const winnerBonus = (TOURNAMENT_WINNER && p.winner &&
      p.winner.trim().toLowerCase() === TOURNAMENT_WINNER.trim().toLowerCase()) ? 7 : 0;
    totalPts += winnerBonus;
    const accuracy = predicted > 0 ? Math.round((correctOutcomes / predicted) * 100) : 0;
    return { name: p.name, totalPts, exactScores, correctOutcomes, accuracy, nepCorrect, nepTotal, predicted, winnerBonus };
  }).filter(s => s.predicted > 0);

  if (stats.length === 0) return <div className="empty-state"><p>Ennustusi pole veel</p></div>;

  const sorted       = [...stats].sort((a,b) => b.totalPts - a.totalPts);
  const bestAccuracy = [...stats].sort((a,b) => b.accuracy - a.accuracy);
  const bestExact    = [...stats].sort((a,b) => b.exactScores - a.exactScores);
  const bestNep      = stats.filter(s => s.nepTotal > 0).sort((a,b) => b.nepCorrect - a.nepCorrect);
  const maxPts       = sorted[0]?.totalPts || 1;

  const StatRow = ({ rank, name, value, sub, max }) => (
    <div className="stat-row">
      <span className="stat-rank">{rank <= 3 ? ["🥇","🥈","🥉"][rank-1] : rank}</span>
      <div className="stat-name-bar">
        <span className="stat-name">{name}</span>
        {max && <div className="bar-track" style={{flex:1}}>
          <div className="bar-fill" style={{width: `${Math.round((value/max)*100)}%`}} />
        </div>}
      </div>
      <div className="stat-value-wrap">
        <span className="stat-value">{value}</span>
        {sub && <span className="stat-sub">{sub}</span>}
      </div>
    </div>
  );

  return (
    <div>
      <div className="stats-section">
        <div className="stats-title">🏆 Punktiseis</div>
        {sorted.map((s,i) => (
          <StatRow key={s.name} rank={i+1} name={s.name}
            value={`${s.totalPts}p`} sub={`${s.predicted} mängu`} max={null} />
        ))}
      </div>

      <div className="stats-section">
        <div className="stats-title">🎯 Tulemusete täpsus</div>
        {bestAccuracy.map((s,i) => (
          <StatRow key={s.name} rank={i+1} name={s.name}
            value={`${s.accuracy}%`}
            sub={`${s.correctOutcomes}/${s.predicted}`} max={null} />
        ))}
      </div>

      <div className="stats-section">
        <div className="stats-title">🔢 Täpsed skoorid</div>
        {bestExact.map((s,i) => (
          <StatRow key={s.name} rank={i+1} name={s.name}
            value={s.exactScores}
            sub={`${s.predicted > 0 ? Math.round(s.exactScores/s.predicted*100) : 0}%`} max={null} />
        ))}
      </div>

      {bestNep.length > 0 && (
        <div className="stats-section">
          <div className="stats-title">⏱️ N/E/P täpsus (playoff)</div>
          {bestNep.map((s,i) => (
            <StatRow key={s.name} rank={i+1} name={s.name}
              value={`${s.nepCorrect}/${s.nepTotal}`}
              sub={`${Math.round(s.nepCorrect/s.nepTotal*100)}%`} max={null} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Nime modal ─────────────────────────────────────────────────────────────
function NameModal({ players, allPlayers, current, playerIndex, onSelect, onClose, onSaveName }) {
  const [step, setStep]     = useState("pick");   // "pick" | "new"
  const [newName, setNewName] = useState("");
  const [error, setError]   = useState("");
  const inputRef = useRef(null);

  useEffect(() => { if (step === "new") inputRef.current?.focus(); }, [step]);

  // Real players = those whose name is NOT a placeholder
  const realPlayers = players.filter(p => !isPlaceholder(p));
  const slotPlayers = players.filter(p => isPlaceholder(p));

  const handlePickExisting = (name) => {
    onSelect(name);
    onClose();
  };

  const handleNewName = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    // Check duplicate (case-insensitive, ignore own current name)
    const isDupe = allPlayers.some(p =>
      p.toLowerCase() === trimmed.toLowerCase() && p !== current
    );
    if (isDupe) { setError(`"${trimmed}" on juba kasutusel`); return; }

    // Find a free slot (first placeholder)
    const freeSlotIdx = players.findIndex(p => isPlaceholder(p));
    if (freeSlotIdx === -1) {
      setError("Kõik kohad on täidetud"); return;
    }
    const ok = await onSaveName(freeSlotIdx, trimmed);
    if (ok) { onSelect(trimmed); onClose(); }
    else setError("Salvestamine ebaõnnestus, proovi uuesti");
  };

  const handleChangeName = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    const isDupe = allPlayers.some(p =>
      p.toLowerCase() === trimmed.toLowerCase() && p !== current
    );
    if (isDupe) { setError(`"${trimmed}" on juba kasutusel`); return; }
    const ok = await onSaveName(playerIndex, trimmed);
    if (ok) { onSelect(trimmed); onClose(); }
    else setError("Salvestamine ebaõnnestus");
  };

  const isReturning = current && !isPlaceholder(current);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        {step === "pick" && (
          <>
            <h2 className="modal-title">Vali nimi</h2>

            {realPlayers.length > 0 && (
              <>
                <p className="modal-hint">Osalejad:</p>
                <div className="modal-list">
                  {realPlayers.map(p => (
                    <button key={p} className={`modal-item ${current === p ? "modal-item-active" : ""}`}
                      onClick={() => handlePickExisting(p)}>
                      {p}
                      {current === p && <Icon.Check />}
                    </button>
                  ))}
                </div>
              </>
            )}

            {isReturning && (
              <button className="modal-change-btn" style={{marginTop: 8}}
                onClick={() => { setStep("new"); setNewName(current); }}>
                ✏️ Muuda oma nime
              </button>
            )}

            {slotPlayers.length > 0 && (
              <>
                <div className="modal-divider">uus osaleja</div>
                <button className="modal-new-btn" onClick={() => setStep("new")}>
                  + Lisa uus nimi
                </button>
              </>
            )}
          </>
        )}

        {step === "new" && (
          <>
            <h2 className="modal-title">
              {isReturning ? "Muuda nime" : "Sisesta oma nimi"}
            </h2>
            <div className="modal-custom">
              <input
                ref={inputRef}
                className="modal-input"
                value={newName}
                onChange={e => { setNewName(e.target.value); setError(""); }}
                placeholder="Sinu nimi..."
                onKeyDown={e => {
                  if (e.key === "Enter") isReturning ? handleChangeName() : handleNewName();
                  if (e.key === "Escape") setStep("pick");
                }}
              />
              <button
                className="modal-confirm"
                disabled={!newName.trim()}
                onClick={isReturning ? handleChangeName : handleNewName}
              >
                Salvesta
              </button>
            </div>
            {error && <p className="modal-error">{error}</p>}
            <button className="modal-back" onClick={() => { setStep("pick"); setError(""); }}>
              ← Tagasi
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Mängukaart (automaatne salvestamine) ───────────────────────────────────
function MatchCard({ match, prediction, nep, result, onSave }) {
  const [homeGoals, setHomeGoals] = useState(prediction ? prediction.split("-")[0] : "");
  const [awayGoals, setAwayGoals] = useState(prediction ? prediction.split("-")[1] : "");
  const [nepVal, setNepVal]       = useState(nep || "");
  const [status, setStatus]       = useState(prediction ? "saved" : "empty"); // empty|saving|saved|error
  const saveTimer = useRef(null);

  const isPlayoff = !!match.round;
  const done = isMatchDone(result);
  const pts  = done && prediction ? calcPoints(result, prediction, nep, isPlayoff) : null;
  const maxPts = isPlayoff ? 7 : 5;

  // Auto-save when both fields filled
  const tryAutoSave = useCallback(async (h, a, n) => {
    const score = `${h}-${a}`;
    if (!isValidScore(score)) return;
    if (isPlayoff && !n) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setStatus("saving");
      const ok = await onSave(match.id, score, n);
      setStatus(ok ? "saved" : "error");
      if (ok) setTimeout(() => setStatus("saved"), 2000);
    }, 400);
  }, [match.id, isPlayoff, onSave]);

  const handleHome = (val) => {
    setHomeGoals(val);
    if (val !== "" && awayGoals !== "") tryAutoSave(val, awayGoals, nepVal);
  };
  const handleAway = (val) => {
    setAwayGoals(val);
    if (homeGoals !== "" && val !== "") tryAutoSave(homeGoals, val, nepVal);
  };
  const handleNep = (val) => {
    setNepVal(val);
    if (homeGoals !== "" && awayGoals !== "" && val) tryAutoSave(homeGoals, awayGoals, val);
  };

  const statusIcon = () => {
    if (status === "saving") return <span className="auto-status saving">⟳</span>;
    if (status === "saved" && (homeGoals !== "" || awayGoals !== ""))
      return <span className="auto-status saved"><Icon.Check /></span>;
    if (status === "error") return <span className="auto-status error">!</span>;
    return null;
  };

  return (
    <div className={`match-card ${done ? "match-done" : ""}`}>
      <div className="match-meta">
        <span className="match-date">{match.date} · {match.time}</span>
        <span className={`match-tag ${done ? "tag-done" : "tag-upcoming"}`}>
          {match.group ? `Gr ${match.group}` : match.label}
          {done && ` · ${result}`}
        </span>
      </div>
      <div className="teams-row">
        <span className="team-name">{match.home || "?"}</span>
        <span className="vs-sep">—</span>
        <span className="team-name team-away">{match.away || "?"}</span>
      </div>
      {done ? (
        <div className="pred-done-row">
          <span className="pred-score">{prediction || "—"}{isPlayoff && nep ? ` ${nep}` : ""}</span>
          {pts !== null && <span className={`pts-badge ${ptsBadge(pts, maxPts)}`}>{pts}p</span>}
        </div>
      ) : (
        <div className="pred-input-area">
          <div className="score-inputs">
            <input className="goal-input" type="number" min="0" max="20"
              value={homeGoals}
              onChange={e => handleHome(e.target.value)}
              placeholder="0" />
            <span className="score-dash">-</span>
            <input className="goal-input" type="number" min="0" max="20"
              value={awayGoals}
              onChange={e => handleAway(e.target.value)}
              placeholder="0" />
            {isPlayoff && (
              <select className="nep-select-inline" value={nepVal} onChange={e => handleNep(e.target.value)}>
                <option value="">—</option>
                <option value="N">N</option>
                <option value="E">E</option>
                <option value="P">P</option>
              </select>
            )}
            {statusIcon()}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tulemuste kaart ─────────────────────────────────────────────────────────
function ResultCard({ match, result, onSave }) {
  const isPlayoff = !!match.round;
  const [score, setScore]   = useState(result?.split(" ")[0] || "");
  const [nepVal, setNepVal] = useState(result?.split(" ")[1] || "");
  const [saved, setSaved]   = useState(!!result);

  const handleSave = async () => {
    if (!isValidScore(score)) return;
    const full = isPlayoff && nepVal ? `${score} ${nepVal}` : score;
    const ok = await onSave(match.id, full);
    if (ok) { setSaved(true); setTimeout(() => setSaved(false), 2500); }
  };

  return (
    <div className={`match-card ${result ? "match-done" : ""}`}>
      <div className="match-meta">
        <span className="match-date">{match.date} · {match.time}</span>
        <span className={`match-tag ${result ? "tag-done" : "tag-upcoming"}`}>
          {match.group ? `Gr ${match.group}` : match.label}
        </span>
      </div>
      <div className="teams-row">
        <span className="team-name">{match.home || "?"}</span>
        <span className="vs-sep">—</span>
        <span className="team-name team-away">{match.away || "?"}</span>
      </div>
      <div className="result-input-row">
        <input className="result-input" value={score}
          onChange={e => { setScore(e.target.value); setSaved(false); }} placeholder="2-1" />
        {isPlayoff && (
          <select className="nep-select" value={nepVal}
            onChange={e => { setNepVal(e.target.value); setSaved(false); }}>
            <option value="">—</option>
            <option value="N">N</option>
            <option value="E">E</option>
            <option value="P">P</option>
          </select>
        )}
        <button className={`confirm-btn ${saved ? "confirm-btn-saved" : ""}`} onClick={handleSave}>
          {saved ? <Icon.Check /> : "Kinnita"}
        </button>
      </div>
    </div>
  );
}

// ── Edetabel ───────────────────────────────────────────────────────────────
function StandingsView({ standings, loading, players, allPlayerData, groupResults, playoffResults }) {
  const medals = ["🥇","🥈","🥉"];
  if (loading) return <div className="loading">Laen…</div>;
  if (!standings.length) return <div className="empty-state"><p>Andmed puuduvad</p></div>;

  const standingsWithNames = standings.map((s, i) => ({
    ...s,
    name: players && players[i] ? players[i] : s.name,
  }));

  // KOKKU lehe SORT/LARGE valemid lähevad katki kui mängijatel on viigis punktid
  // (#REF! vead), mistõttu Sheetsi standings ei ole usaldusväärne. Arvutame
  // edetabeli alati otse ennustustest+tulemustest (sama loogika mis StatsView's).
  const hasRealPlayers = standingsWithNames.some(s => !isPlaceholder(s.name));

  let real;
  if (hasRealPlayers && allPlayerData && allPlayerData.length > 0) {
    // Fallback: arvuta punktid otse ennustustest+tulemustest (sama loogika mis StatsView's)
    const allMatches = [...GROUP_MATCHES, ...PLAYOFF_MATCHES];
    const getResult = (match) => {
      if (match.round) {
        return (playoffResults || {})[match.id] || "";
      }
      return (groupResults || {})[match.id] || "";
    };
    real = allPlayerData
      .filter(p => !isPlaceholder(p.name))
      .map(p => {
        let group = 0, playoff = 0;
        allMatches.forEach(m => {
          const pred = p.predictions?.[m.id];
          if (!pred?.score) return;
          const result = getResult(m);
          if (!isMatchDone(result)) return;
          const pts = calcPoints(result, pred.score, pred.nep, !!m.round) || 0;
          if (m.round) playoff += pts; else group += pts;
        });
        const winnerBonus = (TOURNAMENT_WINNER && p.winner &&
          p.winner.trim().toLowerCase() === TOURNAMENT_WINNER.trim().toLowerCase()) ? 7 : 0;
        return { name: p.name, group, playoff, winnerBonus, total: group + playoff + winnerBonus };
      })
      .sort((a, b) => b.total - a.total);
  } else {
    real = standingsWithNames
      .filter(p => !isPlaceholder(p.name))
      .sort((a, b) => b.total - a.total);
  }

  const max = real[0]?.total || 1;
  return (
    <div className="standings-wrap">
      <table className="standings-table">
        <thead>
          <tr>
            <th>#</th><th>Nimi</th>
            <th className="num-col">AG</th>
            <th className="num-col">PO</th>
            <th className="num-col total-col">Kokku</th>
          </tr>
        </thead>
        <tbody>
          {real.map((p, i) => (
            <tr key={p.name + i + "s"} className={i < 3 ? `top-${i+1}` : ""}>
              <td className="rank-cell">{medals[i] || i+1}</td>
              <td>
                <div className="name-bar-wrap">
                  <span className="player-name">{p.name}</span>
                  <div className="bar-track">
                    <div className="bar-fill" style={{ width: `${Math.round((p.total/max)*100)}%` }} />
                  </div>
                </div>
              </td>
              <td className="num-col">{p.group}</td>
              <td className="num-col">{p.playoff}</td>
              <td className="num-col total-col">{p.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Koondtabel ─────────────────────────────────────────────────────────────
// Returns row bg class based on points
function rowClass(pts, max) {
  if (pts === null || pts === undefined) return "";
  if (pts === max)  return "row-perfect";
  if (pts >= 3)     return "row-good";
  if (pts > 0)      return "row-partial";
  return "row-zero";
}

function SummaryView({ allPlayerData, groupResults, playoffResults, loading }) {
  const [expandedMatch, setExpandedMatch] = useState(null);
  const [filter, setFilter] = useState("played");

  const getResult = (match) => {
    if (match.round) {
      return playoffResults[match.id] || "";
    }
    return groupResults[match.id] || "";
  };

  const allMatches = [...GROUP_MATCHES, ...PLAYOFF_MATCHES];
  // Sort all matches chronologically using SHEET_TO_MATCH order
  const chronoMatches = [...GROUP_MATCHES]
    .sort((a, b) => (MATCH_TO_SHEET[a.id] ?? 999) - (MATCH_TO_SHEET[b.id] ?? 999))
    .concat(PLAYOFF_MATCHES);

  const filtered = chronoMatches.filter(m => {
    const r = getResult(m);
    if (filter === "played")   return isMatchDone(r);
    if (filter === "upcoming") return !isMatchDone(r);
    return true;
  });

  const realPlayers = allPlayerData.filter(p => !isPlaceholder(p.name));

  if (loading) return <div className="loading">Laen…</div>;

  // Winner predictions from allPlayerData
  const winnerPreds = realPlayers
    .map(p => ({ name: p.name, winner: p.winner || "" }))
    .filter(p => p.winner);

  return (
    <div>
      {/* Võitja ennustused */}
      {winnerPreds.length > 0 && (
        <div className="winner-summary-card">
          <div className="winner-summary-title">🏆 Turniiri võitja ennustused</div>
          <div className="winner-summary-grid">
            {realPlayers.map(p => (
              <div key={p.name} className={`winner-summary-item ${p.winner ? "has-pick" : "no-pick"}`}>
                <span className="winner-summary-name">{p.name}</span>
                <span className="winner-summary-pick">{p.winner || "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="chips-row">
        {[{k:"played",l:"Mängitud"},{k:"upcoming",l:"Tulevased"},{k:"all",l:"Kõik"}].map(f => (
          <button key={f.k} className={`chip ${filter===f.k?"chip-active":""}`}
            onClick={() => setFilter(f.k)}>{f.l}</button>
        ))}
      </div>

      {/* Legend */}
      {filter !== "upcoming" && (
        <div className="summary-legend">
          <span className="legend-item legend-perfect">5p / 7p täiuslik</span>
          <span className="legend-item legend-good">õige tulemus</span>
          <span className="legend-item legend-partial">osaliselt õige</span>
          <span className="legend-item legend-zero">vale</span>
        </div>
      )}

      {filtered.length === 0 && (
        <div className="empty-state"><span className="empty-icon">⚽</span><p>Ühtegi mängu ei leitud</p></div>
      )}

      {filtered.map(match => {
        const result   = getResult(match);
        const isOpen   = expandedMatch === match.id;
        const isPlayoff = !!match.round;
        const maxPts   = isPlayoff ? 7 : 5;

        // Pre-calculate all pts for header summary bar
        const allPts = result ? realPlayers.map(p => {
          const pred = p.predictions?.[match.id];
          return pred?.score ? calcPoints(result, pred.score, pred.nep, isPlayoff) : null;
        }) : [];
        const scored  = allPts.filter(x => x !== null);
        const perfect = scored.filter(x => x === maxPts).length;
        const avg     = scored.length ? Math.round(scored.reduce((a,b) => a + (b||0), 0) / scored.length * 10) / 10 : null;

        return (
          <div key={match.id} className="summary-card">
            <button className="summary-header"
              onClick={() => setExpandedMatch(isOpen ? null : match.id)}>
              <div className="summary-match-info">
                <span className="match-date">{match.date} · {match.time}</span>
                <span className="summary-teams">{match.home || "?"} — {match.away || "?"}</span>
              </div>
              <div className="summary-right">
                {result && <span className="summary-result">{result}</span>}
                {result && avg !== null && (
                  <span className="summary-avg">⌀ {avg}p</span>
                )}
                {result && perfect > 0 && (
                  <span className="summary-perfect">{perfect}× täiuslik</span>
                )}
                <span className="summary-chevron">{isOpen ? "▲" : "▼"}</span>
              </div>
            </button>

            {isOpen && (
              <div className="summary-body">
                <table className="summary-table">
                  <thead>
                    <tr>
                      <th>Mängija</th>
                      <th>Ennustus</th>
                      {isPlayoff && <th>N/E/P</th>}
                      {result && <th className="pts-col">PT</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {realPlayers.map((p, pi) => {
                      const pred = p.predictions?.[match.id];
                      const pts  = result && pred?.score
                        ? calcPoints(result, pred.score, pred.nep, isPlayoff) : null;
                      const rc   = result ? rowClass(pts, maxPts) : "";

                      // Highlight exact score match
                      const exactScore = result && pred?.score &&
                        result.split(" ")[0] === pred.score;

                      return (
                        <tr key={p.name} className={rc}>
                          <td className="summary-player">{p.name}</td>
                          <td className={`summary-score ${exactScore ? "exact-score" : ""}`}>
                            {pred?.score || <span className="no-pred">—</span>}
                          </td>
                          {isPlayoff && (
                            <td className={pred?.nep && result && result.includes(pred.nep) ? "nep-correct" : ""}>
                              {pred?.nep || <span className="no-pred">—</span>}
                            </td>
                          )}
                          {result && (
                            <td className="pts-col">
                              {pts !== null
                                ? <span className={`pts-badge ${ptsBadge(pts, maxPts)}`}>{pts}p</span>
                                : <span className="no-pred">—</span>}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ── Võitja ennustuse kaart ─────────────────────────────────────────────────
function WinnerCard({ value, onSave }) {
  const [input, setInput]   = useState(value || "");
  const [status, setStatus] = useState(value ? "saved" : "empty");
  const saveTimer = useRef(null);

  useEffect(() => { setInput(value || ""); setStatus(value ? "saved" : "empty"); }, [value]);

  const handleChange = (val) => {
    setInput(val);
    setStatus("empty");
    clearTimeout(saveTimer.current);
    if (val.trim().length >= 2) {
      saveTimer.current = setTimeout(async () => {
        setStatus("saving");
        const ok = await onSave(val.trim());
        setStatus(ok ? "saved" : "error");
      }, 800);
    }
  };

  return (
    <div className="winner-card">
      <div className="winner-label">
        <span className="winner-trophy">🏆</span>
        <div>
          <div className="winner-title">Turniiri võitja</div>
          <div className="winner-hint">+7 punkti täpse ennustuse eest</div>
        </div>
      </div>
      <div className="winner-input-row">
        <input
          className="winner-input"
          value={input}
          onChange={e => handleChange(e.target.value)}
          placeholder="Sisesta riigi nimi..."
        />
        <span className={`auto-status ${status === "saving" ? "saving" : status === "saved" && input ? "saved" : status === "error" ? "error" : ""}`}>
          {status === "saving" ? "⟳" : status === "saved" && input ? <Icon.Check /> : status === "error" ? "!" : ""}
        </span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// PEAMINE APP
// ══════════════════════════════════════════════════════════════════
export default function App() {
  const [tab, setTab]           = useState("dashboard");
  const [players, setPlayers]   = useState([]);
  const [player, setPlayer]     = useState(() => localStorage.getItem("mm2026_player") || "");
  const [playerIndex, setPlayerIndex] = useState(-1);
  const [showNameModal, setShowNameModal] = useState(false);
  const [groupResults, setGroupResults]   = useState({});  // {matchId: result}
  const [playoffResults, setPlayoffResults] = useState({});
  const [standings, setStandings]         = useState([]);
  const [playerPredictions, setPlayerPredictions] = useState({});
  const [allPlayerData, setAllPlayerData] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [groupFilter, setGroupFilter]   = useState("all");
  const [winnerPrediction, setWinnerPrediction] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [groupMatchRows, setGroupMatchRows] = useState(null);

  // Show modal on first visit (no saved player)
  useEffect(() => {
    if (!localStorage.getItem("mm2026_player")) setShowNameModal(true);
  }, []);

  useEffect(() => {
    async function init() {
      setLoading(true);
      try {
        // 1 call: nimed + tulemused + edetabel
        const { names, results, standings: stand, groupMatchRows: gmr } = await loadInitialData();
        setGroupMatchRows(gmr);
        setPlayers(names);
        setGroupResults(results.groupById || {});
        setPlayoffResults(results.playoffById || {});
        setStandings(stand);
        setLastRefresh(new Date());

        // Restore saved player
        const saved = localStorage.getItem("mm2026_player");
        const savedIdx = saved ? names.indexOf(saved) : -1;
        if (savedIdx !== -1) {
          setPlayerIndex(savedIdx);
        } else if (saved) {
          // Name not found in list — clear it
          localStorage.removeItem("mm2026_player");
          setPlayer("");
        }

        // 1 call: kõigi mängijate ennustused koondvaate jaoks
        if (names.length > 0) {
          const realCount = names.length;
          const { agBlockByMatchId, poBlockByMatchId, nepBlockByMatchId, winnerRow } = await loadAllPredictions(realCount, gmr);
          const allData = names.map((name, idx) => {
            const predictions = {};
            GROUP_MATCHES.forEach(m => {
              const row = agBlockByMatchId[m.id] || [];
              const score = row[idx * 2] || "";
              if (score) predictions[m.id] = { score, nep: "" };
            });
            PLAYOFF_MATCHES.forEach(m => {
              const score = poBlockByMatchId[m.id]?.[idx * 4] || "";
              const nep   = nepBlockByMatchId[m.id]?.[idx * 4] || "";
              if (score) predictions[m.id] = { score, nep };
            });
            const winner = winnerRow[idx * 4] || "";
            return { name, predictions, winner };
          });
          setAllPlayerData(allData);
        }
      } catch(e) { console.error("Init viga:", e); }
      setLoading(false);
    }
    init();
  }, []);

  const handleSelectPlayer = useCallback((name) => {
    setPlayer(name);
    localStorage.setItem("mm2026_player", name);
    // Find index synchronously from current players state
    setPlayers(prev => {
      const idx = prev.indexOf(name);
      setPlayerIndex(idx);
      return prev;
    });
    // Clear old predictions immediately so UI shows loading state
    setPlayerPredictions({});
    setWinnerPrediction("");
  }, []);

  // Laeb ennustused Sheetsist iga kord kui mängija või playerIndex muutub
  useEffect(() => {
    if (playerIndex === -1 || !player) return;
    let cancelled = false; // vältib race condition mitmekordsel kiirvahetusel

    async function fetchPreds() {
      try {
        const preds = await loadPlayerPredictions(playerIndex, groupMatchRows);
        if (cancelled) return; // kasutaja vahetas nime uuesti enne vastust
        const map = {};
        GROUP_MATCHES.forEach(m => {
          const score = preds.predById?.[m.id] || "";
          if (score) map[`${player}_${m.id}`] = { score, nep: "" };
        });
        PLAYOFF_MATCHES.forEach(m => {
          const score = preds.playoffById?.[m.id];
          const nep   = preds.playoffNepById?.[m.id] || "";
          if (score) map[`${player}_${m.id}`] = { score, nep };
        });
        setPlayerPredictions(map);
        setWinnerPrediction(preds.winner || "");
      } catch(e) { console.error("Ennustuste laadimine ebaõnnestus:", e); }
    }
    fetchPreds();
    return () => { cancelled = true; };
  }, [playerIndex, player, groupMatchRows]);

  // Save name to Sheet row 2
  const handleSaveName = useCallback(async (slotIndex, newName) => {
    try {
      // ALAGRUPID row 2, col = 8 + slotIndex*2 (T columns)
      const colAG = 8 + slotIndex * 2;
      // PLAYOFF row 2, col = 7 + slotIndex*4
      const colPO = 7 + slotIndex * 4;
      await Promise.all([
        writeValue("ALAGRUPID", 2, colAG, newName),
        writeValue("PLAYOFF",   2, colPO, newName),
      ]);
      setPlayers(prev => {
        const updated = [...prev];
        updated[slotIndex] = newName;
        setPlayerIndex(slotIndex);
        setAllPlayerData(updated.map(n => ({ name: n, predictions: {} })));
        return updated;
      });
      return true;
    } catch(e) { console.error(e); return false; }
  }, []);

  const handleRefresh = useCallback(async () => {
    try {
      const { results, standings: stand, groupMatchRows: gmr } = await loadInitialData();
      setGroupResults(results.groupById || {});
      setPlayoffResults(results.playoff);
      setStandings(stand);
      if (gmr) setGroupMatchRows(gmr);
      setLastRefresh(new Date());
    } catch(e) { console.error(e); }
  }, []);

  // Automaatne värskendus iga 5 minuti tagant
  useEffect(() => {
    const interval = setInterval(handleRefresh, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [handleRefresh]);

  const getResult = useCallback((match) => {
    if (match.round) {
      return playoffResults[match.id] || "";
    }
    return groupResults[match.id] || "";
  }, [groupResults, playoffResults]);

  const handleSavePrediction = useCallback(async (matchId, score, nep) => {
    if (!player || playerIndex === -1) return false;
    setSaving(true);
    try {
      const isPlayoff = matchId >= 100;
      let ok;
      if (isPlayoff) {
        const absRow = playoffMatchIdToRow(matchId);
        if (!absRow) return false;
        ok = await writeValue("PLAYOFF", absRow, 7 + playerIndex * 4, score);
        if (ok && nep) {
          await writeValue("PLAYOFF", absRow, 7 + playerIndex * 4 + 2, nep);
        }
      } else {
        ok = await savePrediction(matchId, playerIndex, score, groupMatchRows);
      }
      if (ok) setPlayerPredictions(prev => ({ ...prev, [`${player}_${matchId}`]: { score, nep } }));
      return ok;
    } catch(e) { console.error(e); return false; }
    finally { setSaving(false); }
  }, [player, playerIndex, groupMatchRows]);

  const handleSaveResult = useCallback(async (matchId, result) => {
    setSaving(true);
    try {
      const isPlayoff = matchId >= 100;
      let ok;
      if (isPlayoff) {
        const absRow = playoffMatchIdToRow(matchId);
        if (!absRow) return false;
        ok = await writeValue("PLAYOFF", absRow, 6, result);
        if (ok) setPlayoffResults(p => ({ ...p, [matchId]: result }));
      } else {
        // Find actual sheet row using groupMatchRows
        const entry = groupMatchRows?.find(r => {
          const mid = (typeof SHEET_TO_MATCH !== "undefined" ? SHEET_TO_MATCH : {})[r.sheetIdx];
          return mid === matchId;
        });
        const sheetRow = entry ? entry.absRow : null;
        if (!sheetRow) return false;
        ok = await writeValue("ALAGRUPID", sheetRow, 7, result);
        if (ok) setGroupResults(p => ({ ...p, [matchId]: result }));
      }
      return ok;
    } catch(e) { console.error(e); return false; }
    finally { setSaving(false); }
  }, [groupMatchRows]);

  const handleSaveWinner = useCallback(async (value) => {
    if (playerIndex === -1) return false;
    try {
      const ok = await saveWinnerPrediction(playerIndex, value);
      if (ok) setWinnerPrediction(value);
      return ok;
    } catch(e) { console.error(e); return false; }
  }, [playerIndex]);

  const getPred = (matchId) => playerPredictions[`${player}_${matchId}`] || null;

  const toggleGroup = (key) => setCollapsedGroups(p => ({ ...p, [key]: !p[key] }));

  // Sort matches chronologically using sheet order (MATCH_TO_SHEET gives position)
  const chronoSort = (matches) => [...matches].sort((a, b) => {
    const siA = MATCH_TO_SHEET[a.id] ?? 999;
    const siB = MATCH_TO_SHEET[b.id] ?? 999;
    return siA - siB;
  });

  const groupedMatches = () => {
    if (groupFilter === "playoff") {
      const sections = {};
      PLAYOFF_MATCHES.forEach(m => {
        if (!sections[m.round]) sections[m.round] = { key: m.round, label: ROUND_LABELS[m.round] || m.round, matches: [] };
        sections[m.round].matches.push(m);
      });
      return Object.values(sections);
    }
    let src = groupFilter === "all" ? GROUP_MATCHES : GROUP_MATCHES.filter(m => m.group === groupFilter);
    src = chronoSort(src);
    if (groupFilter !== "all") return [{ key: groupFilter, label: `Grupp ${groupFilter}`, matches: src }];
    // For "all" — show as single chronological list, no group sections
    return [{ key: "all", label: "Kõik mängud", matches: src }];
  };

  const tabs = [
    { id: "dashboard",   label: "Minu seis",  icon: Icon.Home   },
    { id: "predictions", label: "Ennustused", icon: Icon.Pencil },
    { id: "standings",   label: "Edetabel",   icon: Icon.Trophy },
    { id: "summary",     label: "Koond",      icon: Icon.Grid   },
    { id: "stats",       label: "Statistika", icon: Icon.Bar    },
  ];

  return (
    <div className="app">
      {showNameModal && (
        <NameModal
          players={players}
          allPlayers={players}
          current={player}
          playerIndex={playerIndex}
          onSelect={handleSelectPlayer}
          onClose={() => { if (player) setShowNameModal(false); }}
          onSaveName={handleSaveName}
        />
      )}

      <header className="app-header">
        <div className="header-inner">
          <div className="header-title">
            <span className="header-eyebrow">Coupe du Monde 2026</span>
            <h1 className="header-h1">CDM 2026</h1>
          </div>
          <div className="header-actions">
            <button className="player-badge" onClick={() => setShowNameModal(true)}>
              <Icon.User />
              <span className="player-badge-name">
                {player && !isPlaceholder(player) ? player : "Vali nimi"}
              </span>
            </button>
            <button className="refresh-btn" onClick={handleRefresh}>
              <Icon.Refresh />
              {lastRefresh && (
                <span className="refresh-time">
                  {lastRefresh.toLocaleTimeString("et-EE", { hour:"2-digit", minute:"2-digit" })}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <nav className="tabs">
        {tabs.map(t => (
          <button key={t.id} className={`tab ${tab===t.id?"tab-active":""}`}
            onClick={() => setTab(t.id)}>
            <t.icon /><span className="tab-label">{t.label}</span>
          </button>
        ))}
      </nav>

      <main className="main-content">

        {tab === "predictions" && (
          <div className="desktop-layout">
            <div className="desktop-main">
              {!player || isPlaceholder(player) ? (
                <div className="empty-state">
                  <span className="empty-icon">⚽</span>
                  <p>Vali ülalt oma nimi et ennustusi sisestada</p>
                  <button className="cta-btn" onClick={() => setShowNameModal(true)}>Vali nimi</button>
                </div>
              ) : (
                <>
                  <div className="chips-row">
                    <button className={`chip ${groupFilter==="all"?"chip-active":""}`} onClick={() => setGroupFilter("all")}>Kõik grupid</button>
                    <button className={`chip ${groupFilter==="playoff"?"chip-active":""}`} onClick={() => setGroupFilter("playoff")}>Playoff</button>
                    {GROUPS.map(g => (
                      <button key={g} className={`chip ${groupFilter===g?"chip-active":""}`}
                        onClick={() => setGroupFilter(g)}>Gr {g}</button>
                    ))}
                  </div>
                  <WinnerCard value={winnerPrediction} onSave={handleSaveWinner} />

                  {loading ? <div className="loading">Laen…</div> : (
                    groupedMatches().map(section => (
                      <div key={section.key} className="match-section">
                        <button className="section-header" onClick={() => toggleGroup(section.key)}>
                          <span>{section.label}</span>
                          {collapsedGroups[section.key] ? <Icon.ChevronRight /> : <Icon.ChevronDown />}
                        </button>
                        {!collapsedGroups[section.key] && (
                          <div className="match-list">
                            {section.matches.map(match => {
                              const pred = getPred(match.id);
                              return (
                                <MatchCard key={match.id} match={match}
                                  prediction={pred?.score||""} nep={pred?.nep||""}
                                  result={getResult(match)} onSave={handleSavePrediction} />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </>
              )}
            </div>
            <div className="desktop-side">
              <div className="side-title">Edetabel</div>
              <StandingsView standings={standings} loading={loading} players={players} allPlayerData={allPlayerData} groupResults={groupResults} playoffResults={playoffResults} />
            </div>
          </div>
        )}



        {tab === "standings" && <StandingsView standings={standings} loading={loading} players={players} allPlayerData={allPlayerData} groupResults={groupResults} playoffResults={playoffResults} />}

        {tab === "dashboard" && (
          <DashboardView
            player={player} playerIndex={playerIndex} players={players}
            groupResults={groupResults} playoffResults={playoffResults}
            playerPredictions={playerPredictions} standings={standings}
            winnerPrediction={winnerPrediction} loading={loading}
            onTabSwitch={setTab} allPlayerData={allPlayerData}
          />
        )}

        {tab === "summary" && (
          <SummaryView allPlayerData={allPlayerData} groupResults={groupResults}
            playoffResults={playoffResults} loading={loading} />
        )}

        {tab === "stats" && (
          <StatsView allPlayerData={allPlayerData} groupResults={groupResults}
            playoffResults={playoffResults} loading={loading} />
        )}

      </main>
    </div>
  );
}
